import { useEffect, useRef, useState } from 'react';
import type { Chat } from '../lib/api';
import { lastMessage } from '../lib/archive';
import { ChatMenu } from './ChatMenu';

/** Same hashing the bubbles use, so a person keeps one colour everywhere. */
const PALETTE = ['#e5786d', '#5f9ea0', '#b07d62', '#7b8fbf', '#8a9a5b', '#b0729b', '#6f8faf'];
const colourFor = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length]!;
};

/** WhatsApp shows a clock today, a weekday this week, a date before that. */
function when(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const days = Math.floor((+new Date(now.toDateString()) - +new Date(d.toDateString())) / 86400000);
  if (days <= 0) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (days === 1) return 'Yesterday';
  if (days < 7) return d.toLocaleDateString(undefined, { weekday: 'short' });
  return d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** WhatsApp names an export "WhatsApp Chat with X"; the useful half is X. */
const shortTitle = (t: string) =>
  t.replace(/^WhatsApp Chat (with|-|–)\s*/i, '').trim() || t;

function Row({ chat, accountKey, active, onOpen, title: raw, photo, actions }: {
  chat: Chat; accountKey: Uint8Array; active: boolean; onOpen: () => void;
  title: string; photo?: string;
  actions: RowActions;
}) {
  const title = shortTitle(raw);
  const [preview, setPreview] = useState<{ who?: string; text: string } | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const pick = useRef<HTMLInputElement>(null);

  useEffect(() => { setEditing(false); setDraft(title); }, [title]);

  // After the list has painted, not before: the preview costs a fetch and a
  // decrypt per chat, and a row is useful without it.
  useEffect(() => {
    if (!chat.directoryCid) return;
    let live = true;
    void lastMessage(accountKey, chat.directoryCid)
      .then((m) => { if (live) setPreview(m); })
      .catch(() => {});
    return () => { live = false; };
  }, [chat.directoryCid, accountKey]);

  const done = (save: boolean) => {
    setEditing(false);
    if (save) actions.onRename(chat, draft);
    else setDraft(title);
  };

  return (
    <li className={active ? 'on' : ''} onClick={() => { if (!editing) onOpen(); }}>
      <span className="face" aria-hidden="true"
            style={photo ? undefined : { background: colourFor(title) }}>
        {photo ? <img src={photo} alt="" /> : title.slice(0, 1).toUpperCase()}
      </span>
      <span className="body">
        <span className="top">
          {editing ? (
            <input className="rename" value={draft} maxLength={200} autoFocus
                   aria-label="Conversation name"
                   onClick={(e) => e.stopPropagation()}
                   onChange={(e) => setDraft(e.target.value)}
                   onBlur={() => done(true)}
                   onKeyDown={(e) => {
                     if (e.key === 'Enter') { e.preventDefault(); done(true); }
                     if (e.key === 'Escape') { e.preventDefault(); done(false); }
                   }} />
          ) : <span className="title">{title}</span>}
          <time>{when(chat.lastMessageAt)}</time>
        </span>
        <span className="sub">
          {preview
            ? <>{preview.who && <b>{preview.who}: </b>}{preview.text}</>
            : `${chat.messageCount.toLocaleString()} messages`}
        </span>
      </span>

      {/* WhatsApp does not put these in the conversation's own header: they
          belong to the row, where the conversation is a thing among others
          rather than the thing you are reading. */}
      <input ref={pick} type="file" accept="image/*" hidden
             onClick={(e) => e.stopPropagation()}
             onChange={(e) => {
               const f = e.target.files?.[0];
               if (f) actions.onPhoto(chat, f);
               e.target.value = '';
             }} />
      <ChatMenu glyph="chevron" className="row-menu" items={[
        { label: 'Rename…', onSelect: () => { setDraft(title); setEditing(true); } },
        { label: photo ? 'Change the picture…' : 'Add a picture…',
          onSelect: () => pick.current?.click() },
        ...(photo ? [{ label: 'Remove the picture',
                       onSelect: () => actions.onPhoto(chat, null) }] : []),
        { label: actions.exportingId !== chat.chatId
            ? 'Export this conversation'
            : actions.exportProgress?.total
              ? `Exporting ${actions.exportProgress.done}/${actions.exportProgress.total}…`
              : 'Exporting…',
          disabled: actions.exportingId !== null,
          onSelect: () => actions.onExport(chat) },
        { label: 'Remove this conversation…', danger: true,
          disabled: actions.exportingId !== null,
          onSelect: () => actions.onRemove(chat) },
      ]} />
    </li>
  );
}

export interface RowActions {
  onRename: (chat: Chat, name: string) => void;
  onPhoto: (chat: Chat, file: File | null) => void;
  onExport: (chat: Chat) => void;
  onRemove: (chat: Chat) => void;
  /** The conversation being exported right now, if any. */
  exportingId: string | null;
  /** How far along, so the count the header used to show is not simply lost. */
  exportProgress: { done: number; total: number } | null;
}

export function ChatList({ chats, accountKey, activeId, onOpen, onHowTo, titleOf, avatarOf,
                           actions }: {
  chats: Chat[];
  accountKey: Uint8Array;
  activeId?: string;
  onOpen: (c: Chat) => void;
  onHowTo: () => void;
  /** Rename, picture, export, remove — everything done to a whole chat. */
  actions: RowActions;
  /** Titles are sealed, so opening one is asynchronous; App does it once. */
  titleOf: (c: Chat) => string;
  /** Likewise the picture, which arrives as an object URL or not at all. */
  avatarOf: (c: Chat) => string | undefined;
}) {
  return (
    <ul className="chats">
      {chats.map((c) => (
        <Row key={c.chatId} chat={c} accountKey={accountKey} title={titleOf(c)}
             photo={avatarOf(c)} actions={actions}
             active={activeId === c.chatId} onOpen={() => onOpen(c)} />
      ))}
      {chats.length === 0 && (
        <li className="empty">
          No chats yet.{' '}
          <button className="link" onClick={onHowTo}>How do I get an export?</button>
        </li>
      )}
    </ul>
  );
}
